import noUiSlider from "nouislider";



import type { ICreateTableCmd } from "@/data/commands/table/CreateTableCmd";
import { Factory } from "@/data/dao/Factory";
import { RequestCache } from "@/data/dao/RequestCache";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { TableDao } from "@/data/dao/service/TableDao";
import { DataOrigin, Language, TableShape } from "@/data/model/Enums";
import type { IRoom } from "@/data/model/Room";
import type { ITable } from "@/data/model/Table";
import { Misc } from "@/data/util/Misc";



import { getLanguageString } from "@/shared/localization/language";
import { type VueComponentInstance, createVueComponent } from "@/shared/utils/vue";



import { i18n } from "@/portal/Trans";
import { NumberInput } from "@/portal/formControls/NumberInput";
import { Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";
import { TastierGlobal } from "@/portal/globalAccess";
import TablePartyListView from "@/portal/views/TablePartyListView.vue";


export class TableForm extends BaseEntityForm<ITable, ICreateTableCmd> {
    private $nameField: JQuery;
    private $posIdField: JQuery;
    private tableNumberField: NumberInput;

    private seatSlider: any;
    private $seatsField: JQuery;

    private $preferredMinimumOccupancySection: JQuery;
    private preferredMinimumOccupancySlider: any;
    private $preferredMinimumOccupancyField?: JQuery;
    private $priorityAssignmentField?: JQuery;
    private $priorityAssignmentSection: JQuery;
    private assignmentPrioritySlider: any;
    private allowSeveralPartiesSwitch: Switch;
    private roomSelect: Select<IRoom>;
    private shapSelect: Select<TableShape>;
    private requireInfoCheck: Switch;
    private isActiveCheck: Switch;
    private isVisibleOnlineCheck: Switch;

    private $editPosIdBtn: JQuery;
    private $posIdRow: JQuery;
    private $tablePartiesSection: JQuery;
    private $allowSeveralPartiesForm: JQuery;
    private tablePartyListView: VueComponentInstance<typeof TablePartyListView>;
    private minimumSeatsBetweenPartiesInput: NumberInput;

    constructor(element: JQuery) {
        super(element, Factory.getInstance(TableDao));

        this.$editPosIdBtn = this.$form.find(".editposid");
        this.$posIdRow = this.$form.find("#posidrow");
        this.$editPosIdBtn.click((e) => {
            e.preventDefault();
            this.togglePosIdVisibility();
        });

        this.$nameField = this.$form
            .find('.form-control[name="name"]')
            .change(() => this.updateName(this.$nameField.val()));
        this.$posIdField = this.$form.find('.form-control[name="posid"]');

        this.tableNumberField = new NumberInput(this.$form.find('.form-control[name="tablenumber"]'), {
            min: 1,
            max: 100000,
            step: 1,
            buttondown_class: "btn btn-secondary",
            buttonup_class: "btn btn-secondary",
            verticalbuttons: true,
            verticalup: '<i class="la la-plus"></i>',
            verticaldown: '<i class="la la-minus"></i>',
        });

        this.tableNumberField.onChange = () => {
            this.updateFieldsByTableNumber(this.tableNumberField.getValue());
        };

        this.roomSelect = Select.createFromSelect(
            this.$form.find('.form-control[name="room"]'),
            RequestCache.roomDataSource(),
            (c) => c.name
        );

        this.$seatsField = this.$form.find('.form-control[name="seats"]');
        this.seatSlider = noUiSlider.create(this.$form.find("#seatslider")[0], {
            start: [2],
            step: 1,
            format: {
                to: (value) => Math.round(value),
                from: (value) => parseInt(value),
            },
            range: {
                min: [0],
                max: [16],
            },
        });

        this.$preferredMinimumOccupancySection = this.$form.find(".preferred-minimum-occupancy-section");
        this.$priorityAssignmentSection = this.$form.find(".priority-assignment-section");
        this.$preferredMinimumOccupancyField = this.$form.find('.form-control[name="preferredMinimumOccupancyField"]');
        this.preferredMinimumOccupancySlider = noUiSlider.create(this.$form.find("#preferred-minimum-occupancy")[0], {
            start: [1],
            step: 1,
            format: {
                to: (value) => Math.round(value),
                from: (value) => parseInt(value),
            },
            range: {
                min: [1],
                max: [16],
            },
        });


        this.$preferredMinimumOccupancyField.change(() => {
            this.preferredMinimumOccupancySlider.set(this.$preferredMinimumOccupancyField!.val());
        });

        this.preferredMinimumOccupancySlider.on("update", (values, handle) => {
            this.$preferredMinimumOccupancyField!.val(values[handle]);
        });

        this.$priorityAssignmentField = this.$form.find('.form-control[name="priorityAssignmentField"]');

        this.assignmentPrioritySlider = noUiSlider.create(this.$form.find("#priority-assignment")[0], {
            start: [1],
            step: 1,
            format: {
                to: (value) => Math.round(value),
                from: (value) => parseInt(value),
            },
            range: {
                min: [1],
                max: [4],
            },
        });

        this.$priorityAssignmentField.change(() => {
            this.assignmentPrioritySlider.set(this.$priorityAssignmentField!.val());
        });

        this.assignmentPrioritySlider.on("update", (values, handle) => {
            this.$priorityAssignmentField!.val(values[handle]);
        });

        this.$seatsField.change(() => {
            this.seatSlider.set(this.$seatsField.val());
        });

        this.seatSlider.on("update", (values, handle) => {
            this.$seatsField!.val(values[handle]);

            if (this.$seatsField!.val() > 0) {
                this.$preferredMinimumOccupancySection?.show();
                this.$priorityAssignmentSection?.show();
                this.$allowSeveralPartiesForm?.show();
            } else {
                this.$preferredMinimumOccupancySection?.hide();
                this.$priorityAssignmentSection?.hide();
                this.$allowSeveralPartiesForm?.hide();
                this.allowSeveralPartiesSwitch.setValue(false);
            }

            this.preferredMinimumOccupancySlider.updateOptions(
                {
                    range: {
                        min: [1],
                        max: [parseInt(values[handle])],
                    },
                },
                true
            );
        });

        const shapeSelect = this.$form.find('.form-control[name="shape"]');
        Misc.eachEnum(TableShape, (i, e) => {
            shapeSelect.append('<option value="' + i + '">' + i18n("enums.TableShape." + e) + "</option>");
        });

        this.shapSelect = Select.createFromSelect(shapeSelect);
        this.requireInfoCheck = new Switch(this.$form.find('input[name="requireinfo"]'));
        this.isActiveCheck = new Switch(this.$form.find('input[name="isactive"]'));
        this.isVisibleOnlineCheck = new Switch(this.$form.find('input[name="isvisibleonline"]'));

        this.minimumSeatsBetweenPartiesInput = new NumberInput(
            this.$form.find('input[name="minimum-seats-between-parties"]'),
            {
                min: 0,
            }
        );

        this.$tablePartiesSection = this.$form.find(".table-parties-section");

        this.$allowSeveralPartiesForm = this.$form.find("#allow-several-parties-form");
        this.allowSeveralPartiesSwitch = new Switch(this.$form.find('input[name="allow-several-parties"]'));
        this.allowSeveralPartiesSwitch.onChange = (isEnabled) => {
            this.tablePartyListView.setData(isEnabled, []);
            this.setTablePartiesSectionVisibility(isEnabled);
        };

        this.tablePartyListView = createVueComponent(
            TablePartyListView,
            {},
            this.$form.find(".table-parties-table-section")[0]
        ).mountedComponent;
    }

    public setFormData(model?: ITable) {
        this.updateName(model === undefined ? "" : model.name);
        this.$nameField.val(model === undefined ? "" : model.name);
        this.$posIdField.val(model === undefined ? "" : model.posId);
        this.tableNumberField.setValue(model === undefined ? undefined : model.tableNumber);
        this.seatSlider.set(model === undefined ? 2 : model.seats);
        this.preferredMinimumOccupancySlider.set(model === undefined ? 1 : model.preferredMinimumOccupancy);
        this.assignmentPrioritySlider.set(model === undefined ? 1 : model.assignmentPriority);

        this.requireInfoCheck.setValue(model === undefined ? false : model.requireInfo);
        this.isActiveCheck.setValue(model === undefined ? true : model.isActive);
        this.isVisibleOnlineCheck.setValue(model === undefined ? false : model.isVisibleOnline);
        this.allowSeveralPartiesSwitch.setValue(model === undefined ? false : model.allowSeveralSplits);
        this.tablePartyListView.setData(
            model?.allowSeveralSplits ?? false,
            model === undefined ? [] : model.tableSplits
        );
        this.setTablePartiesSectionVisibility(model?.allowSeveralSplits ?? false);
        this.minimumSeatsBetweenPartiesInput.setValue(model?.minimumSeatsBetweenParties);

        if (model !== undefined) this.roomSelect.setValue(model.roomId);

        this.shapSelect.setValue(model === undefined ? TableShape.Rectangle : model.shape);

        if (model?.dataOrigin === DataOrigin.Pos) {
            this.isReady = false;
            this.$nameField.prop("readonly", true);
            this.$posIdField.prop("readonly", true);
            this.tableNumberField.setDisabled(true);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateTableCmd {
        this.minimumSeatsBetweenPartiesInput.setError(false);
        this.$seatsField.removeClass("is-invalid");

        const tablePartyGetValidatedDataResult = this.tablePartyListView.getValidatedData();
        const seats = parseInt(this.seatSlider.get());
        const data: ICreateTableCmd = {
            seats: seats,
            preferredMinimumOccupancy: seats === 0 ? undefined : parseInt(this.preferredMinimumOccupancySlider.get()),
            isActive: this.isActiveCheck.getValue(),
            requireInfo: this.requireInfoCheck.getValue(),
            name: this.$nameField.val().length > 0 ? this.$nameField.val() : undefined,
            posId: this.$posIdField.val().length > 0 ? this.$posIdField.val() : undefined,
            roomId: this.roomSelect.getValue()!,
            shape: parseInt(this.shapSelect.getValue()!),
            tableNumber: this.tableNumberField.getValue()!,
            isVisibleOnline: this.isVisibleOnlineCheck.getValue(),
            allowSeveralSplits: this.allowSeveralPartiesSwitch.getValue(),
            tableSplits: tablePartyGetValidatedDataResult.tableSplits ?? [],
            minimumSeatsBetweenParties: this.minimumSeatsBetweenPartiesInput.getValue(),
            assignmentPriority: seats === 0 ? undefined : parseInt(this.assignmentPrioritySlider.get()),
        };

        let hasError = false;

        if (this.allowSeveralPartiesSwitch.getValue()) {
            if (data.minimumSeatsBetweenParties == null || data.minimumSeatsBetweenParties < 0) {
                hasError = true;
                this.minimumSeatsBetweenPartiesInput.setError(true);
            }
        }

        if (!tablePartyGetValidatedDataResult.isValid) {
            hasError = true;
        }

        if (data.seats === undefined || isNaN(data.seats)) {
            hasError = true;
        }

        if (data.name === undefined) {
            hasError = true;
            this.$nameField.addClass("is-invalid");
        }

        if (data.posId === undefined) {
            hasError = true;
            this.$posIdField.addClass("is-invalid");
            if (!this.$posIdRow.hasClass("shown")) this.togglePosIdVisibility();
        }

        if (data.roomId === undefined) {
            hasError = true;
            this.roomSelect.setError(true);
        }

        if (data.name === undefined || isNaN(data.tableNumber) || data.tableNumber <= 0) {
            hasError = true;
            this.tableNumberField.setError(true);
        }

        if (data.allowSeveralSplits) {
            // validate seats
            const totalSeats = data.seats;
            const totalSeatsBetweenParties = data.tableSplits.length * (data.minimumSeatsBetweenParties ?? 0);
            const totalSplitsMaximumSize = data.tableSplits
                .map((it) => it.maximumSize)
                .reduce((sum, current) => sum + current, 0);

            if (totalSeats < totalSeatsBetweenParties + totalSplitsMaximumSize) {
                hasError = true;
                this.$seatsField.addClass("is-invalid");
            }
        }

        return (hasError ? undefined : data) as any;
    }

    private togglePosIdVisibility() {
        if (this.$posIdRow.hasClass("shown")) {
            this.$editPosIdBtn.html('<i class="la la-edit"></i> ' + i18n("table.editPosId"));
            this.$posIdRow.removeClass("shown");
        } else {
            this.$editPosIdBtn.html('<i class="la la-floppy-o"></i> ' + i18n("table.hidePosId"));
            this.$posIdRow.addClass("shown");
        }
    }

    private setTablePartiesSectionVisibility(show: boolean) {
        if (show) {
            this.$tablePartiesSection.show();
        } else {
            this.$tablePartiesSection.hide();
        }
    }

    private updateFieldsByTableNumber(withNumber?: number) {
        if (withNumber === undefined || isNaN(withNumber)) {
            this.$posIdField.val("");
            return;
        }

        if (this.$posIdField.val().length === 0) {
            this.$posIdField.val("t" + withNumber);
        } else {
            const existingPosId = this.$posIdField.val();
            const nrMatch = existingPosId.match(/\d+/g);
            if (nrMatch != null && nrMatch.length > 0) {
                this.$posIdField.val(existingPosId.replace(/\d+/g, withNumber));
            }
        }

        if (this.$nameField.val().length === 0) {
            const languageString = getLanguageString(
                TastierGlobal.getLocationSwitch()?.getCurrentLocationLanguage() ?? Language.English
            );
            const tableInLocationLanguage = i18n("table.table", {
                lng: languageString,
            });
            this.$nameField.val(`${tableInLocationLanguage} ${withNumber}`);
        } else {
            const existingTableName = this.$nameField.val();
            const nrMatch = existingTableName.match(/\d+/g);
            if (nrMatch != null && nrMatch.length > 0) {
                this.$nameField.val(existingTableName.replace(/\d+/g, withNumber));
            }
        }
    }

    protected resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        switch (b.additionalData.reason) {
            case "table_number_in_use":
                return i18n("table.tableNumerInUse");
            case "table_limit_exceeded":
                return i18n("modal.tableBulk.failedLimitExceeded");
            case "no_active_subscription":
                return i18n("modal.tableBulk.failedNoSubscription");
        }

        return undefined;
    }
}
